import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Input, Label } from 'reactstrap';

import apiPdfEnd, { OfferOwner } from 'api/offerPdfEnd';
import translations from 'services/translations/translations';
import { addMessage } from 'store/messages/actions';

export type PdfSection =
  | 'showDeliveryTerms'
  | 'showExtraPages'
  | 'showPriceList';

/**
 * Whether a section at the end of the offer PDF (the additional price list, the delivery
 * terms or the extra pages) is printed. Left out, its content is kept in the offer. Saved
 * at once.
 */
const PdfSectionShown = ({
  locked,
  owner,
  ownerId,
  section,
}: {
  locked: boolean;
  owner: OfferOwner;
  ownerId: number;
  section: PdfSection;
}): JSX.Element | null => {
  const dispatch = useDispatch();
  const { t } = useTranslation([translations.offers]);
  const [shown, setShown] = useState<boolean>();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    apiPdfEnd.pdfSettings
      .get(owner, ownerId)
      .then((settings) => active && setShown(settings[section]))
      .catch(() => active && setShown(undefined));
    return () => {
      active = false;
    };
  }, [owner, ownerId, section]);

  if (shown === undefined) {
    return null;
  }

  const toggle = async () => {
    const next = !shown;
    setSaving(true);
    setShown(next);

    try {
      await apiPdfEnd.pdfSettings.patch(owner, ownerId, { [section]: next });
    } catch (error) {
      setShown(!next);
      dispatch(addMessage(t(`${translations.offers}:pdfPrices.Save failed`)));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Label check className="pdf-section-shown">
      <Input
        checked={shown}
        disabled={locked || saving}
        onChange={toggle}
        type="checkbox"
      />{' '}
      {t(`${translations.offers}:pdfSections.${section}`)}
      {shown ? null : (
        <span className="pdf-section-hidden">
          {t(`${translations.offers}:pdfSections.Hidden`)}
        </span>
      )}
    </Label>
  );
};

export default PdfSectionShown;
